import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "../../chat/api";
import { showErrorToast, showSuccessToast } from "../../../utils/toast";

// The owner's queue of "can I have access to this?" asks.
//
// A bell notification can tell you somebody asked, but it can't carry the
// answer — granting access means choosing a permission, so it needs somewhere
// to happen. This is that somewhere.
//
// Only the project's OWNER can answer. A workspace admin who can see the
// project but doesn't own it holds no power over it, which is the same line
// the transfer-ownership endpoint draws: visibility is not control.
const PERMISSIONS = [
  { value: "reviewer", label: "Reviewer", hint: "can read and comment" },
  { value: "editor", label: "Editor", hint: "can also edit project fields" },
];

const AccessRequestsModal = ({ isOpen, onClose, onResolved }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  // folder_id -> chosen permission, defaulting to the lower of the two.
  const [permissions, setPermissions] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await api.get("/api/v1/rfp/access-requests/?box=incoming&status=pending");
      setRequests(res.data?.requests || []);
    } catch {
      setLoadError("Couldn't load access requests.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoading(true);
    setLoadError("");
    api
      .get("/api/v1/rfp/access-requests/?box=incoming&status=pending")
      .then((res) => {
        if (!cancelled) setRequests(res.data?.requests || []);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Couldn't load access requests.");
        setRequests([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const respond = async (request, action) => {
    if (busyId) return;
    setBusyId(request.id);
    try {
      const permission = permissions[request.id] || "reviewer";
      await api.post(`/api/v1/rfp/access-requests/${request.id}/respond/`, {
        action,
        ...(action === "approve" ? { permission } : {}),
      });

      const who = request.requester?.name || request.requester?.email || "They";
      showSuccessToast(
        action === "approve"
          ? `${who} now has ${permission} access to ${request.folder?.name}.`
          : `Declined ${who}'s request.`
      );

      // Drop it from the queue rather than refetching the whole list — the
      // answer is final and the row has nothing left to show.
      setRequests((prev) => prev.filter((r) => r.id !== request.id));
      if (onResolved) onResolved();
    } catch (error) {
      const message =
        error?.response?.data?.error || "Couldn't answer that request.";
      showErrorToast(message);
      // A 409 means somebody already dealt with it, so the row is stale.
      if (error?.response?.status === 409) load();
    } finally {
      setBusyId(null);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center font-outfit">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-2xl shadow-xl w-[620px] max-h-[85vh] flex flex-col px-6 py-5">
        <h2 className="text-[#18181B] text-[20px] font-semibold mb-1">Access requests</h2>
        <p className="text-[#71717A] text-[14px] leading-relaxed mb-4">
          {loading
            ? "Loading…"
            : requests.length === 0
            ? "Nobody is waiting on access to your projects."
            : `${requests.length} ${
                requests.length === 1 ? "person is" : "people are"
              } asking for access to your projects. Choose what they get.`}
        </p>

        {requests.length > 0 && (
          <div className="flex-1 overflow-y-auto border border-[#E4E4E7] rounded-lg divide-y divide-[#F4F4F5] min-h-0">
            {requests.map((r) => {
              const busy = busyId === r.id;
              return (
                <div key={r.id} className="px-3 py-3">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-[14px] text-[#18181B] truncate">
                        <span className="font-medium">
                          {r.requester?.name || r.requester?.email || "Someone"}
                        </span>{" "}
                        wants access to{" "}
                        <span className="font-medium">{r.folder?.name}</span>
                      </p>
                      {r.message && (
                        <p className="text-[13px] text-[#71717A] mt-0.5 break-words">
                          “{r.message}”
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={permissions[r.id] || "reviewer"}
                      onChange={(e) =>
                        setPermissions((prev) => ({ ...prev, [r.id]: e.target.value }))
                      }
                      disabled={busy}
                      className="px-2 py-1.5 border border-[#E4E4E7] rounded-md text-[13px] bg-white outline-none focus:border-[#C95EBE]"
                    >
                      {PERMISSIONS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label} ({p.hint})
                        </option>
                      ))}
                    </select>

                    <button
                      onClick={() => respond(r, "approve")}
                      disabled={busy}
                      className="px-4 py-1.5 rounded-md bg-[#C95EBE] text-white text-[13px] font-semibold hover:bg-[#B14DAA] transition-colors disabled:opacity-60"
                    >
                      {busy ? "Working…" : "Grant access"}
                    </button>
                    <button
                      onClick={() => respond(r, "deny")}
                      disabled={busy}
                      className="px-4 py-1.5 rounded-md border border-[#E4E4E7] text-[#52525B] text-[13px] font-medium hover:bg-gray-50 disabled:opacity-60"
                    >
                      Decline
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {loadError && <p className="text-[13px] text-[#E21919] mt-3">{loadError}</p>}

        <div className="flex justify-end mt-5">
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-md border border-[#E4E4E7] text-[#52525B] text-[14px] font-medium hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AccessRequestsModal;
